/**
 * 上限時間に入りきらず外れたスポット
 *
 * 消えたわけではないことを示し、プランから完全に外すか判断できるようにする。
 */

import { CalendarDays, CircleSlash, Pin, X } from 'lucide-react'

import type { RoutePlanItem } from '@/api/types/route-plan'
import { Button } from '@/components/ui/button'

import { PlaceMapsLink } from '../../components/place-maps-link'
import { PlanDayHoursChip } from './plan-day-hours-chip'
import { StaySettingChip } from './plan-timeline'

type Props = {
  items: RoutePlanItem[]
  isBusy: boolean
  /** 共有されたプラン。外す操作は出さない */
  readOnly?: boolean
  /** 「必ず立ち寄る」・滞在時間・到着希望の設定を開く */
  onEdit: (item: RoutePlanItem) => void
  onRemove: (item: RoutePlanItem) => void
  /** 入りきらない分を別の日へ回す導線 */
  onSplitDays?: () => void
}

export const PlanSkippedList = ({ items, isBusy, readOnly = false, onEdit, onRemove, onSplitDays }: Props) => {
  if (items.length === 0) return null

  const hasRequired = items.some((item) => item.is_required)

  return (
    <div className="space-y-2 border-t p-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <CircleSlash size={14} />
        <span>時間内に回りきれなかったスポット（{items.length}）</span>
      </div>
      {hasRequired ? (
        <p className="text-xs text-destructive">
          必ず立ち寄るスポットも入りきりませんでした。上限時間を延ばすか、他のスポットを外してください
        </p>
      ) : (
        !readOnly && (
          <p className="text-xs text-muted-foreground">
            時間のチップから「必ず立ち寄る」にすると、再最適化で優先して残します
          </p>
        )
      )}
      {items.map((item) => (
        <div key={item.id} className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm">{item.name}</span>
            <div className="flex items-center gap-2">
              <StaySettingChip item={item} readOnly={readOnly} onEdit={onEdit} />
              <PlanDayHoursChip dayHours={item.day_hours} />
            </div>
          </div>
          {item.is_required && <Pin size={13} className="shrink-0 text-violet-600" aria-label="必ず立ち寄る" />}
          <PlaceMapsLink item={item} />
          {!readOnly && (
            <Button
              size="sm"
              variant="ghost"
              className="size-7 shrink-0 p-0! text-muted-foreground"
              disabled={isBusy}
              onClick={() => onRemove(item)}
              aria-label="プランから外す"
            >
              <X size={14} />
            </Button>
          )}
        </div>
      ))}
      {!readOnly && onSplitDays && (
        <Button variant="outline" className="w-full gap-2" disabled={isBusy} onClick={onSplitDays}>
          <CalendarDays size={16} />
          日帰りで何日かに分けて全部回る
        </Button>
      )}
    </div>
  )
}
